// Pure helpers of the shared-space screens (COL-FE-01): the tabs the server allows, how a person
// reached the space, the settings draft and plain-language error messages.
import type {
  MySpaceListDtoOutputItemsItem,
  SpaceDtoOutput,
  UpdateSpaceDto,
} from '@batiflow/api-client';
import { ApiError } from '@batiflow/client-core';
import { UpdateSpaceRequestSchema } from '@batiflow/contracts';
import { formatDate, type AppLocale } from '@batiflow/i18n';
import type { TFunction } from 'i18next';

export type Space = SpaceDtoOutput;
export type MySpace = MySpaceListDtoOutputItemsItem;
export type SpaceTab = Space['tabs'][number];
export type Translate = TFunction<'spaces'>;

/** Display order of the tabs (the server only says which ones the caller may open). */
export const SPACE_TAB_ORDER = [
  'documents',
  'synthesis',
  'groupement',
  'chat',
  'calendar',
  'meetings',
  'people',
  'partners',
  'delegates',
  'settings',
  'audit',
] as const satisfies readonly SpaceTab[];

export const isSpaceTab = (value: string): value is SpaceTab =>
  (SPACE_TAB_ORDER as readonly string[]).includes(value);

/** The tabs of the space the caller may open, in display order (an external sees what it reaches). */
export function visibleTabs(space: Pick<Space, 'tabs'>): SpaceTab[] {
  return SPACE_TAB_ORDER.filter((tab) => space.tabs.includes(tab));
}

export const isClosed = (space: Pick<Space, 'status'>): boolean => space.status === 'closed';

export const canManageSpace = (space: Pick<Space, '_permissions'>): boolean =>
  space._permissions.includes('can_manage');

/** Left-to-right isolate and its end: a date keeps its order inside an Arabic sentence. */
const LRI = String.fromCharCode(0x2066);
const PDI = String.fromCharCode(0x2069);

/** The sentence parts of how the caller reached an external space: way, rights, end date. */
export function accessParts(
  access: Pick<Space['access'], 'via' | 'rights' | 'expiresAt'>,
  t: Translate,
  locale: AppLocale,
): string[] {
  const parts = [t('access.via', { via: t(`via.${access.via}`) })];
  if (access.rights) parts.push(t('access.rights', { rights: t(`rights.${access.rights}`) }));
  parts.push(
    access.expiresAt
      ? t('access.until', { date: `${LRI}${formatDate(access.expiresAt, locale)}${PDI}` })
      : t('access.noEnd'),
  );
  return parts;
}

// --- Settings draft -----------------------------------------------------------------------------

export interface SettingsValues {
  name: string;
  downloadAllowed: boolean;
  watermark: boolean;
  printAllowed: boolean;
  shareLinksAllowed: boolean;
  guestDomains: string[];
}

export const valuesOf = (space: Space): SettingsValues => ({
  name: space.name,
  downloadAllowed: space.policy.downloadAllowed,
  watermark: space.policy.watermark,
  printAllowed: space.policy.printAllowed,
  shareLinksAllowed: space.policy.shareLinksAllowed,
  guestDomains: [...space.policy.guestDomains],
});

const NAME_MAX = 200;

export type PatchResult =
  | { ok: true; data: UpdateSpaceDto | null }
  | { ok: false; reason: 'nameRequired' | 'nameTooLong' | 'invalid' };

/**
 * The PATCH body made of what changed: the name, and the whole policy when any of its rules
 * changed (the API replaces the policy as one object). `data: null` means nothing changed.
 */
export function buildSettingsPatch(space: Space, values: SettingsValues): PatchResult {
  const name = values.name.trim();
  if (name.length === 0) return { ok: false, reason: 'nameRequired' };
  if (name.length > NAME_MAX) return { ok: false, reason: 'nameTooLong' };
  const { policy } = space;
  const policyChanged =
    values.downloadAllowed !== policy.downloadAllowed ||
    values.watermark !== policy.watermark ||
    values.printAllowed !== policy.printAllowed ||
    values.shareLinksAllowed !== policy.shareLinksAllowed ||
    values.guestDomains.length !== policy.guestDomains.length ||
    values.guestDomains.some((domain, index) => domain !== policy.guestDomains[index]);
  const data: UpdateSpaceDto = {
    ...(name !== space.name ? { name } : {}),
    ...(policyChanged
      ? {
          policy: {
            downloadAllowed: values.downloadAllowed,
            watermark: values.watermark,
            printAllowed: values.printAllowed,
            shareLinksAllowed: values.shareLinksAllowed,
            guestDomains: values.guestDomains,
          },
        }
      : {}),
  };
  if (Object.keys(data).length === 0) return { ok: true, data: null };
  // The contract's own rule decides (domains, name length): the same one the server applies.
  return UpdateSpaceRequestSchema.safeParse(data).success
    ? { ok: true, data }
    : { ok: false, reason: 'invalid' };
}

// --- Errors -------------------------------------------------------------------------------------

export const apiErrorCode = (error: unknown): string | null =>
  error instanceof ApiError ? error.code : null;

/** 403 STEP_UP_REQUIRED: the app opens its step-up dialog and replays the action itself. */
export const isStepUpRequired = (error: unknown): boolean =>
  apiErrorCode(error) === 'STEP_UP_REQUIRED';

export const isPlanLimit = (error: unknown): boolean =>
  apiErrorCode(error) === 'PLAN_LIMIT_REACHED';

/** A plain-language message for a refused save or creation (never the server's raw text). */
export function spaceErrorMessage(
  t: Translate,
  tCommon: TFunction<'common'>,
  error: unknown,
  scope: 'settings' | 'create',
): string {
  if (error instanceof ApiError) {
    if (error.code === 'FORBIDDEN') return t(`${scope}.errors.forbidden`);
    if (error.code === 'VALIDATION_FAILED' && scope === 'settings') {
      return t('settings.errors.validation');
    }
    if (error.status === 404 && scope === 'create') return t('create.errors.projectGone');
    return tCommon(`errors.${error.code}`, { defaultValue: tCommon('errors.generic') });
  }
  return tCommon('errors.generic');
}

// --- Lists --------------------------------------------------------------------------------------

/** The spaces of the caller's organisations, then the external ones; each group sorted by name. */
export function groupSpaces(items: readonly MySpace[]): { own: MySpace[]; external: MySpace[] } {
  const byName = (a: MySpace, b: MySpace) => a.name.localeCompare(b.name);
  return {
    own: items.filter((space) => !space.external).sort(byName),
    external: items.filter((space) => space.external).sort(byName),
  };
}
